import { handleServiceError } from '../utils/errorHandler';
import { PaymentSystem } from '../types';
import type { ProjectIdentity } from '../types/project';
import { mapProjectIdentity } from '../utils/project';
import { requestEdgeFunction } from './edgeFunctionClient';

export interface InvalidSubscriptionRecord {
  studentSubscriptionId: number;
  externalSubscriptionId: string;
  canceledAt: string | null;
  studentId: number;
  studentName: string;
  studentEmail: string | null;
  planDescription: string | null;
  planName: string | null;
  project: ProjectIdentity | null;
  paymentSystem: PaymentSystem;
}

type RawInvalidSubscriptionRow = {
  id: number;
  external_subscription_id: string;
  canceled_at: string | null;
  plan_name: string | null;
  plan_description: string | null;
  project?: unknown;
  students_payment_accounts: RawJunction | RawJunction[] | null;
};

type RawJunction = {
  student_id: number;
  students: {
    full_name: string;
    email: string | null;
  } | {
    full_name: string;
    email: string | null;
  }[] | null;
  payment_accounts: {
    payment_system: PaymentSystem;
  } | {
    payment_system: PaymentSystem;
  }[] | null;
};

const unwrapRelation = <T,>(value: T | T[] | null | undefined): T | null => {
  if (Array.isArray(value)) {
    return value[0] ?? null;
  }
  return value ?? null;
};

const hasInvalidExternalSubscriptionId = (value: string): boolean => /\s/.test(value);

const SUPPORTED_GATEWAYS = new Set<PaymentSystem>(['asaas', 'stripe', 'paypal']);

export class FixSubscriptionServiceClass {
  async listInvalidExternalSubscriptionIds(): Promise<InvalidSubscriptionRecord[]> {
    const response = await requestEdgeFunction<{ data?: RawInvalidSubscriptionRow[] }>('payment-gateway', {
      method: 'POST',
      body: {
        action: 'list-invalid-external-subscriptions',
      },
    }).catch((error) => {
      handleServiceError(error, 'listar assinaturas com external_subscription_id inválido');
      throw error;
    });

    const rows = (response.data ?? []) as RawInvalidSubscriptionRow[];

    return rows
      .filter((row) => hasInvalidExternalSubscriptionId(row.external_subscription_id))
      .map((row) => {
        const junction = unwrapRelation(row.students_payment_accounts);
        const student = unwrapRelation(junction?.students ?? null);
        const paymentAccount = unwrapRelation(junction?.payment_accounts ?? null);
        const paymentSystem = paymentAccount?.payment_system ?? 'asaas';

        return {
          studentSubscriptionId: row.id,
          externalSubscriptionId: row.external_subscription_id,
          canceledAt: row.canceled_at,
          studentId: junction?.student_id ?? 0,
          studentName: student?.full_name ?? '—',
          studentEmail: student?.email ?? null,
          planDescription: row.plan_description,
          planName: row.plan_name,
          project: mapProjectIdentity(row.project),
          paymentSystem,
        };
      })
      .filter((row) => row.studentId > 0 && SUPPORTED_GATEWAYS.has(row.paymentSystem));
  }
}

export const FixSubscriptionService = new FixSubscriptionServiceClass();
